const IDENTIFIER  = /^[a-zA-Z_][a-zA-Z0-9_]*/;
const NUMBER      = /^\d+(\.\d+)?$/;
const FILTER_NAME = /^\|\s*[a-zA-Z_][a-zA-Z0-9_]*\s*/;
const OPENERS     = {'(': ')', '[': ']', '{': '}'};

// Scan the doc for Twig delimiters: `{{ ... }}`, `{% ... %}`, `{# ... #}`.
// Returns the start/end positions and a kind tag. Pending placeholders are
// detected up front so the CM plugin can wire its data-pending-start hook.
export function scanTwigSegments(text) {
    const segments = [];
    const n = text.length;
    let i = 0;
    while (i < n) {
        if (text[i] === '{' && text[i + 1] === '{') {
            const end = text.indexOf('}}', i + 2);
            if (end === -1) break;
            const segEnd = end + 2;
            const isPending = text.slice(i, segEnd).toLowerCase().includes('please select');
            segments.push({start: i, end: segEnd, kind: isPending ? 'pending' : 'var'});
            i = segEnd;
            continue;
        }
        if (text[i] === '{' && text[i + 1] === '%') {
            const end = text.indexOf('%}', i + 2);
            if (end === -1) break;
            const segEnd = end + 2;
            const isPending = text.slice(i, segEnd).toLowerCase().includes('please select');
            segments.push({start: i, end: segEnd, kind: isPending ? 'pending' : 'ctrl'});
            i = segEnd;
            continue;
        }
        if (text[i] === '{' && text[i + 1] === '#') {
            const end = text.indexOf('#}', i + 2);
            if (end === -1) break;
            segments.push({start: i, end: end + 2, kind: 'comment'});
            i = end + 2;
            continue;
        }
        i += 1;
    }
    return segments;
}

// Split a {{ }} inner (e.g. " name|upper|date('Y-m-d') ") into a path chunk +
// |filter chunks. Pipes inside quotes, parens, brackets or braces are ignored
// so `|default("a|b")` and `|replace({"|":"/"})` are preserved intact.
export function splitFilterParts(inner) {
    const parts = [];
    let current = '';
    let depth = 0;
    let quote = null;
    for (let i = 0; i < inner.length; i++) {
        const c = inner[i];
        if (quote) {
            current += c;
            if (c === quote && inner[i - 1] !== '\\') quote = null;
            continue;
        }
        if (c === '"' || c === "'") { quote = c; current += c; continue; }
        if (c === '(' || c === '[' || c === '{') { depth++; current += c; continue; }
        if (c === ')' || c === ']' || c === '}') { depth--; current += c; continue; }
        if (c === '|' && depth === 0) { parts.push(current); current = '|'; continue; }
        current += c;
    }
    parts.push(current);
    return parts;
}

const skipQuoted = (source, index) => {
    const quote = source[index];
    let i = index + 1;

    while (i < source.length) {
        if (source[i] === '\\') {
            i += 2;
            continue;
        }

        if (source[i] === quote) {
            return i + 1;
        }

        i += 1;
    }

    return -1;
};

const skipBalanced = (source, index) => {
    const closers = [OPENERS[source[index]]];
    let i = index + 1;

    while (i < source.length && closers.length > 0) {
        const character = source[i];

        if (character === '"' || character === "'") {
            i = skipQuoted(source, i);

            if (i === -1) {
                return -1;
            }

            continue;
        }

        if (OPENERS[character]) {
            closers.push(OPENERS[character]);
        } else if (character === closers[closers.length - 1]) {
            closers.pop();
        } else if (character === ')' || character === ']' || character === '}') {
            return -1;
        }

        i += 1;
    }

    return closers.length === 0 ? i : -1;
};

const skipPrimary = source => {
    const first = source[0];

    if (first === '"' || first === "'") {
        return skipQuoted(source, 0);
    }

    if (OPENERS[first]) {
        return skipBalanced(source, 0);
    }

    return source.match(IDENTIFIER)?.[0].length ?? -1;
};

const isOperand = source => {
    if (NUMBER.test(source)) {
        return true;
    }

    let i = skipPrimary(source);

    while (i !== -1 && i < source.length) {
        const rest = source.slice(i);

        if (rest[0] === '.') {
            const attribute = rest.slice(1).match(/^([a-zA-Z_][a-zA-Z0-9_]*|\d+)/);
            i = attribute ? i + 1 + attribute[0].length : -1;
        } else if (rest[0] === '[' || rest[0] === '(') {
            const end = skipBalanced(rest, 0);
            i = end === -1 ? -1 : i + end;
        } else {
            i = -1;
        }
    }

    return i === source.length;
};

const isFilterCall = part => {
    const name = part.match(FILTER_NAME);

    if (!name) {
        return false;
    }

    const rest = part.slice(name[0].length);

    if (rest === '') {
        return true;
    }

    if (rest[0] !== '(') {
        return false;
    }

    const end = skipBalanced(rest, 0);

    return end !== -1 && rest.slice(end).trim() === '';
};

const isFilterChain = expression => {
    const [operand, ...filters] = splitFilterParts(expression);

    return isOperand(operand.trim()) && filters.every(isFilterCall);
};

const expressionBounds = (text, segment) => {
    let start = segment.start + 2;
    let end   = segment.end - 2;

    if (text[start] === '-' || text[start] === '~') {
        start += 1;
    }

    if (end > start && (text[end - 1] === '-' || text[end - 1] === '~')) {
        end -= 1;
    }

    while (start < end && /\s/.test(text[start])) {
        start += 1;
    }

    while (end > start && /\s/.test(text[end - 1])) {
        end -= 1;
    }

    return {start, end};
};

export const findFilterTarget = (text, from, to) => {
    const matches = scanTwigSegments(text).filter(segment => {
        if (segment.kind !== 'var') {
            return false;
        }

        const bounds = expressionBounds(text, segment);

        return bounds.start < bounds.end && segment.start <= from && to <= segment.end;
    });

    if (matches.length !== 1) {
        return null;
    }

    return {start: matches[0].start, end: matches[0].end};
};

export const buildFilterEdit = (text, target, clause) => {
    const bounds     = expressionBounds(text, target);
    const expression = text.slice(bounds.start, bounds.end);

    if (isFilterChain(expression)) {
        return {from: bounds.end, to: bounds.end, insert: clause};
    }

    return {from: bounds.start, to: bounds.end, insert: `(${expression})${clause}`};
};
